/** A colour group reference as the APIs return it: a bare id, or a populated group document. */
export type ColourGroupRef = string | { _id?: string | null } | null | undefined;

/** The minimum shape a colour needs to be placed in a group list. */
export interface GroupColour {
  _id: string;
  name?: string;
}

/** Result of adding colours to a group's list without repeating any. */
export interface ColourMergeResult<T extends GroupColour> {
  colours: T[];
  alreadyInGroup: T[];
}

/**
 * Normalises a colour group reference to its id. The colour list API returns `groups` as bare
 * ids, while populated endpoints return group documents; callers must handle both.
 *
 * @param {ColourGroupRef} group - A bare group id or a group document.
 * @returns {string | null} The group id, or null when there is none.
 */
export function toGroupId(group: ColourGroupRef): string | null {
  if (!group) {
    return null;
  }
  if (typeof group === 'string') {
    return group;
  }
  return group._id ? String(group._id) : null;
}

/**
 * Appends `incoming` colours to `current`, skipping any colour whose id is already present,
 * so a group never lists the same colour twice.
 *
 * @param {T[]} current - Colours already in the group list.
 * @param {T[]} incoming - Colours being added to it.
 * @returns {ColourMergeResult<T>} The combined list, and the incoming colours that were already present.
 */
export function mergeGroupColours<T extends GroupColour>(current: T[], incoming: T[]): ColourMergeResult<T> {
  const seenIds = new Set<string>();
  const colours: T[] = [];
  const alreadyInGroup: T[] = [];

  (current || []).forEach((colour) => {
    const colourId = colour?._id ? String(colour._id) : '';
    if (colourId && !seenIds.has(colourId)) {
      seenIds.add(colourId);
      colours.push(colour);
    }
  });

  (incoming || []).forEach((colour) => {
    const colourId = colour?._id ? String(colour._id) : '';
    if (!colourId) {
      return;
    }
    if (seenIds.has(colourId)) {
      alreadyInGroup.push(colour);
      return;
    }
    seenIds.add(colourId);
    colours.push(colour);
  });

  return { colours, alreadyInGroup };
}

/**
 * Returns the distinct colour ids of a group list, in order, for the save payload.
 *
 * @param {GroupColour[]} colours - The colours shown in the group.
 * @returns {string[]} Each colour id once.
 */
export function toUniqueColourIds(colours: GroupColour[]): string[] {
  return mergeGroupColours([], colours || []).colours.map((colour) => String(colour._id));
}

/**
 * Normalises a colour's group references, bare ids or group documents, to distinct group ids.
 *
 * @param {ColourGroupRef[] | null | undefined} groups - The colour's groups as an API or a select returns them.
 * @returns {string[]} Each group id once, in order.
 */
export function toGroupIds(groups: ReadonlyArray<ColourGroupRef> | null | undefined): string[] {
  const groupIds = (groups || []).map(toGroupId).filter((groupId): groupId is string => Boolean(groupId));
  return Array.from(new Set(groupIds));
}

/**
 * Returns the groups a colour belonged to that are no longer selected, for the save payload's `removedGroups`.
 *
 * @param {string[]} originalGroupIds - Groups the colour was in when the form opened.
 * @param {string[]} selectedGroupIds - Groups selected now.
 * @returns {string[]} The original group ids that were deselected.
 */
export function getRemovedGroupIds(originalGroupIds: string[], selectedGroupIds: string[]): string[] {
  const selected = new Set(selectedGroupIds);
  return originalGroupIds.filter((groupId) => !selected.has(groupId));
}
